"use strict";
export function initBlobBackground() {
    // 1. Create and Style Canvas
    const canvas = document.createElement('canvas');
    const styles = {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: '-1',
        pointerEvents: 'none',
        display: 'block',
    };
    const className = 'blob-background-canvas';
    canvas.classList.add(className);
    Object.assign(canvas.style, styles);
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    
    let width, height;
    let scrollY = window.scrollY;
    let time = 0;

    // Configuration
    const BREAKPOINT = 992;
    let blobWidthPercent = 0.33;
    let blurAmount = 60;
    let alphaValue = 0.1;
    
    // Base colors (without alpha) - exposed to window
    const blobColors = {
        primary: { r: 119, g: 156, b: 171 },    // #779CAB - right side
        secondary: { r: 240, g: 93, b: 94 }     // #F05D5E - left side
    };
    
    // Transition state
    let targetColors = { ...blobColors };
    let currentColors = { 
        primary: { ...blobColors.primary },
        secondary: { ...blobColors.secondary }
    };
    let transitionProgress = 1; // 1 = complete, 0 = just started
    const transitionSpeed = 0.05;
    
    // Helper to convert RGB object to rgba string
    function rgbToRgba(rgb, alpha) {
        return `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${alpha})`;
    }
    
    // Helper to interpolate between two RGB values
    function lerpRGB(from, to, t) {
        return {
            r: from.r + (to.r - from.r) * t,
            g: from.g + (to.g - from.g) * t,
            b: from.b + (to.b - from.b) * t
        };
    }
    
    // Update colors with smooth transition
    function updateColors(newColors) {
        if (newColors.primary) {
            targetColors.primary = { ...newColors.primary };
        }
        if (newColors.secondary) {
            targetColors.secondary = { ...newColors.secondary };
        }
        transitionProgress = 0;
    }
    
    // Expose to window
    window.blobBackgroundColors = blobColors;
    window.updateBlobBackgroundColors = updateColors;
    
    function handleResize() {
        // Half-resolution for performance
        width = window.innerWidth / 2;
        height = window.innerHeight / 2;
        canvas.width = width;
        canvas.height = height;

        blobWidthPercent = window.innerWidth >= BREAKPOINT ? 0.33 : 0.5;
        blurAmount = window.innerWidth >= BREAKPOINT ? 60 : 10;
        alphaValue = window.innerWidth >= BREAKPOINT ? 0.5 : 0.3;
    }

    function handleScroll() {
        scrollY = window.scrollY;
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    function draw() {
        time += 0.025; // Control animation speed
        
        // Update color transition
        if (transitionProgress < 1) {
            transitionProgress = Math.min(1, transitionProgress + transitionSpeed);
            currentColors.primary = lerpRGB(currentColors.primary, targetColors.primary, transitionSpeed);
            currentColors.secondary = lerpRGB(currentColors.secondary, targetColors.secondary, transitionSpeed);
        }
        
        ctx.clearRect(0, 0, width, height);

        const viewportHeight = window.innerHeight * .75;
        
        // Draw visible blobs
        const startIdx = Math.floor(scrollY / viewportHeight) - 1;
        const endIdx = Math.ceil((scrollY + viewportHeight) / viewportHeight) + 1;

        for (let i = Math.max(0, startIdx); i <= endIdx; i++) {
            const isLeft = i % 2 === 0;
            
            // Horizontal motion (drift left and right)
            const driftX = Math.sin(time + i) * 20; 
            const blobW = Math.max(width * blobWidthPercent, 150);
            
            // The height is now the same as the width for a 1x1 aspect ratio
            const blobH = blobW;
            
            let x = isLeft ? -10 + driftX : (width - blobW + 10) + driftX;
            
            // Vertical motion (drift up and down)
            const driftY = Math.cos(time * 0.8 + i) * 30;
            
            // To center it vertically in the 100vh interval:
            // (Viewport height zone - Blob height) / 2
            const zoneYStart = (i * viewportHeight);
            const centeringOffset = (viewportHeight - (blobH * 2)) / 2; // *2 because canvas is half-res
            
            const y = ((zoneYStart + centeringOffset) - scrollY) / 2 + driftY;

            const centerX = x + (blobW / 2);
            const centerY = y + (blobH / 2);
            const radius = blobW / 2;

            // Use secondary color for left, primary for right
            const colorRGB = isLeft ? currentColors.secondary : currentColors.primary;
            const blobColor = rgbToRgba(colorRGB, alphaValue);

            // Create blurry circle effect
            const gradient = ctx.createRadialGradient(
                centerX, centerY, 0,
                centerX, centerY, radius
            );

            gradient.addColorStop(0, blobColor);
            gradient.addColorStop(0.5, rgbToRgba(colorRGB, alphaValue * 0.5)); // Fade from center
            gradient.addColorStop(1, rgbToRgba(colorRGB, 0)); // Fully transparent at edge

            ctx.fillStyle = gradient;
            ctx.filter = `blur(${blurAmount}px)`; // Responsive blur
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.filter = 'none'; // Reset filter for next iteration
        }

        requestAnimationFrame(draw);
    }

    // Start up
    handleResize();
    draw();

    // Return a cleanup function in case it's used in a React/Vue hook
    return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
        canvas.remove();
    };
}